import { useEffect, useState } from "react";

import {
  accountErrorMessage,
  approveTeamSkillsDeviceLink,
  reviewTeamSkillsDeviceLink,
  type TeamSkillsDeviceLinkReview,
  type WorkspaceSummary,
} from "../../lib/account-client";

const storageKey = "doorway.team-skills-device-link";

export interface CapturedTeamSkillsDeviceLink {
  linkId: string;
  approvalCode: string;
}

export interface TeamSkillsDeviceConnectApi {
  review: typeof reviewTeamSkillsDeviceLink;
  approve: typeof approveTeamSkillsDeviceLink;
}

const defaultApi: TeamSkillsDeviceConnectApi = {
  review: reviewTeamSkillsDeviceLink,
  approve: approveTeamSkillsDeviceLink,
};

export function captureTeamSkillsDeviceLink(
  pathname = window.location.pathname,
): CapturedTeamSkillsDeviceLink | null {
  if (pathname !== "/manage/team/skills/devices/link") return null;
  const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  if (window.location.hash) {
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}`);
    const links = fragment.getAll("link");
    const codes = fragment.getAll("code");
    const validKeys = [...fragment.keys()].every((key) => key === "link" || key === "code");
    if (!validKeys || links.length !== 1 || codes.length !== 1
      || !safeValue(links[0]!) || !safeValue(codes[0]!)) {
      clearTeamSkillsDeviceLinkCapture();
      return null;
    }
    const capture = { linkId: links[0]!, approvalCode: codes[0]! };
    try { window.sessionStorage.setItem(storageKey, JSON.stringify(capture)); } catch { /* ephemeral fallback only */ }
    return capture;
  }
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(storageKey) ?? "null") as Partial<CapturedTeamSkillsDeviceLink> | null;
    return saved && safeValue(saved.linkId ?? "") && safeValue(saved.approvalCode ?? "")
      ? { linkId: saved.linkId!, approvalCode: saved.approvalCode! }
      : null;
  } catch {
    return null;
  }
}

export function clearTeamSkillsDeviceLinkCapture(): void {
  try { window.sessionStorage.removeItem(storageKey); } catch { /* no persistent browser state */ }
}

export function TeamSkillsDeviceConnectPage({
  capture,
  workspaces,
  user,
  api = defaultApi,
  navigate = (url) => window.location.assign(url),
}: {
  capture: CapturedTeamSkillsDeviceLink | null;
  workspaces: WorkspaceSummary[];
  user?: { name: string; email: string; image?: string | null } | undefined;
  api?: TeamSkillsDeviceConnectApi;
  navigate?: (url: string) => void;
}) {
  const available = workspaces;
  const [workspaceId, setWorkspaceId] = useState(available[0]?.id ?? "");
  const [review, setReview] = useState<TeamSkillsDeviceLinkReview | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(capture ? null : "This connection link is incomplete or expired. Start again from Doorway for Mac.");

  useEffect(() => {
    if (!capture || !workspaceId) return;
    let active = true;
    setReview(null);
    setError(null);
    void api.review(capture.linkId, capture.approvalCode, workspaceId).then((result) => {
      if (active) setReview(result);
    }).catch((cause: unknown) => {
      if (active) setError(accountErrorMessage(cause, "Doorway could not review this Mac connection."));
    });
    return () => { active = false; };
  }, [api, capture, workspaceId]);

  const selectedReview = review?.workspaceId === workspaceId ? review : null;
  const selectedWorkspace = available.find((workspace) => workspace.id === workspaceId);
  const teamSkillsEnabled = selectedWorkspace?.capabilities.teamSkills.state === "enabled";

  const connect = async () => {
    if (!capture || !workspaceId || !selectedReview) return;
    setPending(true);
    setError(null);
    try {
      const result = await api.approve(capture.linkId, capture.approvalCode, workspaceId);
      if (!safeCallbackUrl(result.callbackUrl)) throw new Error("Doorway returned an invalid Mac callback.");
      clearTeamSkillsDeviceLinkCapture();
      navigate(result.callbackUrl);
    } catch (cause) {
      setError(accountErrorMessage(cause, "Doorway could not connect this Mac."));
      setPending(false);
    }
  };

  return (
    <main className="workspace-gate mac-connect-gate">
      <section className="flow-card review-stack mac-connect-card" aria-labelledby="team-skills-device-connect">
        <p className="step-label">Doorway for Mac</p>
        <h1 id="team-skills-device-connect">Connect this Mac?</h1>
        {user && <p className="mac-connect-account">Signed in as <strong>{user.email}</strong></p>}
        {available.length === 0 ? (
          <p role="alert">Your Doorway account does not have an active workspace yet.</p>
        ) : (
          <>
            {available.length > 1 && (
              <label className="team-skills-workspace-choice">
                Doorway workspace
                <select value={workspaceId} onChange={(event) => setWorkspaceId(event.target.value)}>
                  {available.map((workspace) => <option key={workspace.id} value={workspace.id}>{workspace.name}</option>)}
                </select>
              </label>
            )}
            <div className="mac-connect-review">
              <div className="mac-connect-device" aria-hidden="true">▣</div>
              <div>
                <strong>{selectedReview?.deviceLabel ?? "Your Mac"}</strong>
                <span>First connection</span>
              </div>
              <div className="mac-connect-workspace">
                <strong>{selectedWorkspace?.name}</strong>
                <span>{selectedReview?.approvingRole ?? selectedWorkspace?.role} access</span>
              </div>
            </div>
            <p className="mac-connect-capability">{teamSkillsEnabled
              ? selectedReview?.approvingRole === "viewer"
                ? "This Mac can use your workspace's shared skills."
                : "This Mac can use shared skills and send your updates to Doorway for review."
              : "This Mac will connect to your Doorway workspace. Team Skills become available on the Team plan."}</p>
            <ul className="mac-connect-boundaries">
              <li>Project folders stay read-only</li>
              <li>Publishing always happens in your browser</li>
            </ul>
            <button className="primary-button team-skills-device-connect-button" type="button" disabled={pending || !selectedReview} onClick={() => void connect()}>
              {pending ? "Connecting..." : "Connect this Mac"}
            </button>
            <a className="mac-connect-cancel" href="/manage">Cancel</a>
          </>
        )}
        {error && <p role="alert">{error}</p>}
      </section>
    </main>
  );
}

function safeValue(value: string): boolean {
  return value.length >= 1 && value.length <= 512 && /^[A-Za-z0-9_-]+$/.test(value);
}

function safeCallbackUrl(value: string): boolean {
  try {
    const url = new URL(value);
    const keys = [...url.searchParams.keys()];
    return url.protocol === "doorway-sync:"
      && url.hostname === "team-skills"
      && url.pathname === "/callback"
      && !url.username && !url.password && !url.port && !url.hash
      && keys.length === 2 && keys.every((key) => key === "code" || key === "state")
      && url.searchParams.getAll("code").length === 1
      && url.searchParams.getAll("state").length === 1
      && safeValue(url.searchParams.get("code") ?? "")
      && safeValue(url.searchParams.get("state") ?? "");
  } catch {
    return false;
  }
}
